import {
  applyCustomAttributeRules,
  FieldTypeMap,
  genFormFields,
  getFormValidation,
} from './generated-helpers'
import { isFieldRequired, type FormFieldMeta, type SubmissionMode } from './types'
import type { FormField } from './generated-types'
import { shouldDisableFutureDates } from './date-constraints'

export function buildFormFieldFromMeta(
  field: FormFieldMeta,
  value: unknown,
  submissionMode: SubmissionMode = 'submit',
): FormField {
  const generatedField = genFormFields({
    ...field,
    required: isFieldRequired(field, submissionMode),
  })

  const formInputType = FieldTypeMap[field.field_type] ?? generatedField.type
  // Build the schema from the field *including* the value it loaded with, so
  // validators that need to distinguish "the user typed this" from "this was
  // already on the record" can do so - see the phone case in
  // `getFormValidation`.
  const validationSchema = applyCustomAttributeRules(
    getFormValidation({ ...generatedField, value }),
    field.validation_rules ?? [],
    formInputType,
  )

  // Past-only date fields (incident / purchase / loss) block future dates.
  // Config-driven via `disable_future` / `allow_future`, with a default
  // allowlist of past-only code_names - see `shouldDisableFutureDates`.
  const disableFuture =
    formInputType === 'datepicker' &&
    shouldDisableFutureDates(
      field.code_name,
      field.configuration_data as Record<string, unknown> | undefined,
    )

  return {
    ...generatedField,
    value,
    validationSchema,
    ...(disableFuture ? { disableFuture: true } : {}),
  }
}
